import cn from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import { getImageUrl } from 'editor/src/store/design/selector/getImageElementUrl';
import getSelectedImageUuid from 'editor/src/store/design/selector/getSelectedImageUuid';
import getImageIdsUsage from 'editor/src/store/design/util/getImageIdsUsage';
import getImageIdsWithPersonalizationLocked from 'editor/src/store/design/util/getImageIdsWithPersonalizationLocked';
import applyImageOperation from 'editor/src/store/editor/operation/applyImageOperation';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import isMultipleImageSelectAllowed from 'editor/src/store/editor/selector/isMultipleImageSelectAllowed';
import isPersonalizationLockIgnored from 'editor/src/store/editor/selector/isPersonalizationLockIgnored';
import isUseNotUploadedImagesAllowed from 'editor/src/store/editor/selector/isUseNotUploadedImagesAllowed';
import addLibraryImageToGalleryOperation from 'editor/src/store/editorModules/customerLibrary/operation/addLibraryImageToGalleryOperation';
import { getLibraryImages, isLibraryEnabled } from 'editor/src/store/editorModules/customerLibrary/selector';
import getGalleryImages from 'editor/src/store/gallery/selector/getGalleryImages';
import getHideUsedPhotos from 'editor/src/store/gallery/selector/getHideUsedPhotos';
import getImageAction from 'editor/src/store/gallery/selector/getImageAction';
import getPdfSplits from 'editor/src/store/gallery/selector/getPdfSplits';
import getSelectedGalleryImagesIds from 'editor/src/store/gallery/selector/getSelectedGalleryImagesIds';
import { selectImageAction, unselectAllImagesAction, unselectImagesAction } from 'editor/src/store/gallery/slice';
import { AssetDigitizationStatus, GalleryImage } from 'editor/src/store/gallery/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import ImageListItem from 'editor/src/component/DesktopSidebar/TabContents/GalleryTabContent/ImageListItem';
import IconInfo from 'editor/src/component/Icon/IconInfo';
import { useIsMobile } from 'editor/src/component/useDetectDeviceType';
import WithTooltip from 'editor/src/component/WithTooltip';
import PreviewDialog from 'product-personalizer/src/component/PreviewView/PreviewDialog';

import GalleryImageTab, { GalleryTabItem } from './GalleryImageTab';
import VerticalMasonry from './VerticalMasonry';

import styles from './index.module.scss';

interface Props {
  onAddImageCb?: () => void;
  extraBottomPadding?: number;
  onDisplayedImagesChange?: (images: GalleryImage[]) => void;
  onLibraryTabChange?: (inLibraryTab: boolean) => void;
}

function GalleryImageList({ onAddImageCb, extraBottomPadding, onLibraryTabChange, onDisplayedImagesChange }: Props) {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<GalleryTabItem>(GalleryTabItem.All);

  const {
    images,
    imageAction,
    designData,
    allowedUseNotUploadedImages,
    allowSelectMultipleImages,
    ignorePersonalizationLock,
    hideUsedPhotos,
    isMultiSelectEnabled,
    canvasSelectedImageUuid,
    selectedGalleryImagesIds,
    libraryEnabled,
    libraryImages,
  } = useSelector((state) => {
    const currentSpreadIndex = getCurrentSpreadIndex(state);
    return {
      images: getGalleryImages(state),
      imageAction: getImageAction(state),
      designData: state.design.designData,
      hideUsedPhotos: getHideUsedPhotos(state),
      currentSpreadIndex,
      allowedUseNotUploadedImages: isUseNotUploadedImagesAllowed(state),
      allowSelectMultipleImages: isMultipleImageSelectAllowed(state),
      ignorePersonalizationLock: isPersonalizationLockIgnored(state),
      isMultiSelectEnabled: isMultipleImageSelectAllowed(state),
      canvasSelectedImageUuid: getSelectedImageUuid(state),
      selectedGalleryImagesIds: getSelectedGalleryImagesIds(state),
      libraryEnabled: isLibraryEnabled(state),
      libraryImages: getLibraryImages(state),
    };
  }, shallowEqual);

  const imageUsage = useMemo(() => getImageIdsUsage(designData), [designData]);

  const pdfSplits = useSelector(getPdfSplits);
  // The grid fills its shortest column first, so pages listed in order can read out of order without their numbers.
  const pdfPageNumbers = useMemo(() => {
    const numbers = new Map<string, number>();
    pdfSplits.forEach((split) =>
      split.pageImageIds?.forEach((imageId, index) => {
        if (imageId) {
          numbers.set(imageId, index + 1);
        }
      }),
    );
    return numbers;
  }, [pdfSplits]);

  const cannotDeleteImageIds = useMemo(() => {
    if (ignorePersonalizationLock) {
      return new Set<string>();
    }
    return getImageIdsWithPersonalizationLocked(designData);
  }, [designData, ignorePersonalizationLock]);

  const onImageClick = useCallback(
    (imageId: string) => {
      if (isMultiSelectEnabled && canvasSelectedImageUuid === undefined) {
        if (selectedGalleryImagesIds.includes(imageId)) {
          dispatch(unselectImagesAction([imageId]));
        } else {
          dispatch(selectImageAction(imageId));
        }
      } else {
        dispatch(applyImageOperation({ imageId }));
        onAddImageCb?.();
      }
    },
    [isMultiSelectEnabled, selectedGalleryImagesIds, canvasSelectedImageUuid],
  );

  const filteredImages = useMemo(
    () => images.filter((image) => !image.hidden && (!hideUsedPhotos || !imageUsage.get(image.id))),
    [images, hideUsedPhotos, imageUsage],
  );

  const onTabSwitch = (key: GalleryTabItem) => {
    if (key !== activeTab) {
      dispatch(unselectAllImagesAction());
      setActiveTab(key);
    }
  };

  const digitizedImages = useMemo(() => {
    return filteredImages.filter(
      (image) =>
        image.digitizationStatus === AssetDigitizationStatus.DIGITIZED ||
        image.digitizationStatus === AssetDigitizationStatus.DIGITIZING,
    );
  }, [filteredImages]);

  const filteredLibraryImages = useMemo(
    () => libraryImages.filter((image) => !hideUsedPhotos || !imageUsage.get(image.id)),
    [libraryImages, hideUsedPhotos, imageUsage],
  );

  const onBeforeLibraryImageUse = useCallback(
    (imageId: string) => dispatch(addLibraryImageToGalleryOperation(imageId)),
    [],
  );

  const inLibraryTab = activeTab === GalleryTabItem.MyLibrary;
  // reported on mount too, since a remounted list starts back on the first tab
  useEffect(() => onLibraryTabChange?.(inLibraryTab), [inLibraryTab]);

  let displayedImages = filteredImages;
  if (activeTab === GalleryTabItem.Digitized) {
    displayedImages = digitizedImages;
  } else if (inLibraryTab) {
    displayedImages = filteredLibraryImages;
  }

  useEffect(() => onDisplayedImagesChange?.(displayedImages), [displayedImages, onDisplayedImagesChange]);

  const [previewImageId, setPreviewImageId] = useState<string | null>(null);

  const previewableImages = useMemo(
    () =>
      displayedImages
        .map((image) => ({ id: image.id, url: getImageUrl(image) }))
        .filter((item): item is { id: string; url: string } => !!item.url),
    [displayedImages],
  );

  const previewIndex = previewImageId ? previewableImages.findIndex((item) => item.id === previewImageId) : -1;

  const onPreview = useCallback((imageId: string) => setPreviewImageId(imageId), []);

  const onPreviewIndexChanged = useCallback(
    (index: number) => setPreviewImageId(previewableImages[index]?.id ?? null),
    [previewableImages],
  );

  const onPreviewClose = useCallback(() => setPreviewImageId(null), []);

  // three tabs share the sidebar width, so the long labels give way to short ones next to My library
  const allLabel = libraryEnabled ? t('editor-library-tab-uploads') : t('All uploaded files');
  const digitizedLabel = libraryEnabled ? t('editor-library-tab-digitized') : t('Digitized files');

  const digitizedTab = {
    key: GalleryTabItem.Digitized,
    label: (
      <>
        {digitizedLabel}&nbsp;
        <WithTooltip
          placement="top"
          overlay={t('After digitization, you can reuse the file for future orders at no extra cost')}
        >
          <IconInfo size="small" />
        </WithTooltip>
      </>
    ),
  };

  return (
    <div className={cn(styles.galleryImageList, 'cy-gallery-image-list')}>
      {(!!digitizedImages.length || libraryEnabled) && (
        <GalleryImageTab
          items={[
            {
              key: GalleryTabItem.All,
              label: allLabel,
            },
            ...(digitizedImages.length ? [digitizedTab] : []),
            ...(libraryEnabled
              ? [
                  {
                    key: GalleryTabItem.MyLibrary,
                    label: t('editor-library-tab-my-library'),
                  },
                ]
              : []),
          ]}
          activeKey={activeTab}
          onSwitch={onTabSwitch}
        />
      )}
      {inLibraryTab && !displayedImages.length && (
        <div className={cn(styles.emptyLibrary, 'cy-my-library-empty')}>{t('editor-library-empty')}</div>
      )}
      <VerticalMasonry<GalleryImage>
        elements={displayedImages}
        cols={3}
        spacing={4}
        minHeight={60}
        maxHeight={150}
        extraBottomPadding={extraBottomPadding}
        render={(image, x, y, width, height) => (
          <ImageListItem
            key={image.uuid}
            image={image}
            imageAction={imageAction}
            isSelected={selectedGalleryImagesIds.includes(image.id)}
            usage={imageUsage.get(image.id) ?? 0}
            pdfPageNumber={pdfPageNumbers.get(image.id)}
            // removing from the library tab must never delete the asset
            noRemove={inLibraryTab || cannotDeleteImageIds.has(image.id)}
            allowedUseNotUploadedImages={allowedUseNotUploadedImages}
            allowMultipleImageSelect={allowSelectMultipleImages}
            isMobile={isMobile}
            onImageClick={onImageClick}
            onPreview={onPreview}
            onBeforeUse={inLibraryTab ? onBeforeLibraryImageUse : undefined}
            inLibraryTab={inLibraryTab}
            x={x}
            y={y}
            width={width}
            height={height}
          />
        )}
      />
      {previewIndex >= 0 && (
        <PreviewDialog
          images={previewableImages.map((item) => item.url)}
          selectedIndex={previewIndex}
          isMobile={isMobile}
          onIndexChanged={onPreviewIndexChanged}
          onClose={onPreviewClose}
          fitContain
        />
      )}
    </div>
  );
}

export default React.memo(GalleryImageList);
